import { useEffect } from 'react';
import { type Embed as EmbedType } from 'types/capi/content';

import {
  type SocialEmbedPlacement,
  type SocialEmbedProvider
} from '~/events/SocialEmbedInteractionEvent';
import useEnv from '~/hooks/useEnv';
import useSocialEmbedClick from '~/hooks/useSocialEmbedClick';
import useSocialEmbedImpression from '~/hooks/useSocialEmbedImpression';
import sdks from '~/utils/sdks';

type Props = {
  embed: EmbedType;
  modulePosition?: number;
  placement: SocialEmbedPlacement;
};

const Embed = ({ embed, modulePosition, placement }: Props) => {
  switch (embed.site) {
    case 'facebook':
      return <FacebookEmbed url={embed.url} />;
    case 'twitter':
      return <TwitterEmbed html={embed.html} />;
    case 'instagram':
    case 'tiktok':
      return (
        <SocialEmbed
          embed={embed}
          modulePosition={modulePosition}
          placement={placement}
          provider={embed.site}
        />
      );
    default:
      return null;
  }
};

const FacebookEmbed = ({ url }: { url: EmbedType['url'] }) => {
  const { FACEBOOK_APP_ID } = useEnv();

  useEffect(() => {
    sdks.injectFacebook(FACEBOOK_APP_ID);
  }, [FACEBOOK_APP_ID]);

  return (
    <div className="custom-scrollbar flex w-full justify-center overflow-x-scroll text-center">
      <div data-href={url} />
    </div>
  );
};

const TwitterEmbed = ({ html }: { html: EmbedType['html'] }) => {
  useEffect(() => {
    sdks.injectTwitter();
  }, []);

  return html ? (
    <div
      className="flex w-full justify-center"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ) : null;
};

const SocialEmbed = ({
  embed,
  modulePosition,
  placement,
  provider
}: Props & { provider: SocialEmbedProvider }) => {
  const containerRef = useSocialEmbedClick(
    embed,
    provider,
    placement,
    modulePosition
  );
  const impressionRef = useSocialEmbedImpression(
    embed,
    provider,
    placement,
    modulePosition
  );

  useEffect(() => {
    if (provider === 'instagram') sdks.injectInstagram();
  }, [embed.html, provider]);

  return embed.html ? (
    <div ref={impressionRef}>
      <div
        className="flex w-full justify-center"
        dangerouslySetInnerHTML={{ __html: embed.html }}
        ref={containerRef}
      />
    </div>
  ) : null;
};

export default Embed;
